/*
  Manmukh Ayurveda Enterprise Framework
  File: assets/ma-reveal.js

  Scroll-reveal engine (Upgrade Blueprint Pillar 2 §2.2, Checklist B item 3).

  Design constraints, in the order they mattered:

  1. NEVER HIDE CONTENT WE MIGHT NOT UN-HIDE. The <html> element only gets
     the `ma-reveal-ready` class — the class the CSS uses to hide anything
     at all — after this script has run and confirmed IntersectionObserver
     exists. A blocked, failed or unsupported script leaves every element
     visible.

  2. NEVER DELAY LCP. Elements that are already inside (or above) the first
     viewport at init are marked shown immediately, without animation. The
     hero, the H1 and the first card row therefore paint at their normal
     time; only content the visitor scrolls to is animated. This is the
     difference between a reveal that helps Core Web Vitals stay green and
     one that tanks LCP.

  3. RESPECT REDUCED MOTION, AND KEEP RESPECTING IT. The preference is
     re-checked on change, so a visitor who turns it on mid-session gets
     everything revealed at once rather than being left with hidden
     content.

  4. ZERO LAYOUT WORK. Only opacity/transform are touched, so the engine
     cannot contribute to Cumulative Layout Shift.

  5. DISCONNECT WHEN DONE. Each element is unobserved as it reveals, and
     the observer disconnects entirely once nothing is left — no listener
     survives to cost INP on a long page.

  Opt-in per element via `data-ma-reveal`. Sections opt in through
  snippets/ma-section-spacing.liquid, which adds the attribute when the
  theme setting is on, so no section file needs to know this exists.
*/
(function () {
  'use strict';

  var doc = document;
  var root = doc.documentElement;
  var REVEALED = 'ma-reveal--shown';
  var READY = 'ma-reveal-ready';

  function prefersReducedMotion() {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function revealAll() {
    var nodes = doc.querySelectorAll('[data-ma-reveal]');
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].classList.add(REVEALED);
    }
    root.classList.remove(READY);
  }

  /*
    Auto-targeting. Sections are opted in from here rather than by editing
    80 section files, and — more importantly — the `data-ma-reveal`
    attribute is applied by the same script that will later remove the
    hidden state. A section can never end up hidden by an attribute baked
    into its markup while the script that reveals it fails to run.

    The selectors below are the two container conventions every ma- section
    already follows: an inner element carrying `data-ma-component`, or a
    bare `.page-width` container. The Theme Editor's own section wrapper is
    never targeted, so editing stays predictable.
  */
  var AUTO_TARGETS = ['.ma-section > [data-ma-component]', '.ma-section > .page-width'];

  function autoTag() {
    for (var s = 0; s < AUTO_TARGETS.length; s++) {
      var found = doc.querySelectorAll(AUTO_TARGETS[s]);
      for (var i = 0; i < found.length; i++) {
        if (!found[i].hasAttribute('data-ma-reveal')) {
          found[i].setAttribute('data-ma-reveal', '');
        }
      }
    }
  }

  function init() {
    // No observer support, or the visitor asked for less motion: show
    // everything, permanently, and never arm anything. Note that autoTag()
    // has not run at this point, so nothing was ever marked reveal-able.
    if (!('IntersectionObserver' in window) || prefersReducedMotion()) {
      revealAll();
      return;
    }

    autoTag();
    root.classList.add(READY);

    var observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var entry = entries[i];
          if (!entry.isIntersecting) continue;
          entry.target.classList.add(REVEALED);
          observer.unobserve(entry.target);
        }

        if (doc.querySelectorAll('[data-ma-reveal]:not(.' + REVEALED + ')').length === 0) {
          observer.disconnect();
        }
      },
      {
        // Start the reveal slightly before the element's top edge arrives,
        // so it finishes as it lands rather than starting once it is
        // already fully read.
        rootMargin: '0px 0px -8% 0px',
        threshold: 0.05
      }
    );

    var viewportHeight = window.innerHeight || root.clientHeight || 0;
    var nodes = doc.querySelectorAll('[data-ma-reveal]');

    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      var rect = node.getBoundingClientRect();

      // Above the fold at load: show it now, with no transition, so the
      // first paint is unaffected.
      if (rect.top < viewportHeight) {
        node.classList.add(REVEALED);
        continue;
      }

      observer.observe(node);
    }

    // A visitor can enable reduced motion at any time.
    if (typeof window.matchMedia === 'function') {
      var query = window.matchMedia('(prefers-reduced-motion: reduce)');
      var onChange = function () {
        if (!query.matches) return;
        observer.disconnect();
        revealAll();
      };

      if (typeof query.addEventListener === 'function') {
        query.addEventListener('change', onChange);
      } else if (typeof query.addListener === 'function') {
        query.addListener(onChange);
      }
    }

    // Sections added by the Theme Editor after load need arming too.
    doc.addEventListener('shopify:section:load', function (event) {
      var added = event.target.querySelectorAll
        ? event.target.querySelectorAll('[data-ma-reveal]')
        : [];
      for (var j = 0; j < added.length; j++) {
        added[j].classList.add(REVEALED);
      }
    });
  }

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
